import React, { useEffect, useMemo, useRef, useState } from 'react';
import { mod, seededRandom } from './index';

// Spot the Difference — emoji-grid version (no art assets needed). Two
// 5×5 grids are shown side by side; a fixed number of cells differ between
// them. Player taps the differing cell on either grid to mark it. Score =
// correct marks - incorrect marks (clamped at 0). Higher WIS = more time
// and a brief "hint" pulse near the start that highlights one undiscovered
// difference.

const GRID_SIZE  = 5;
const DIFF_COUNT = 4;       // number of cells that differ between left/right
const POOL = ['🔴', '🟠', '🟡', '🟢', '🔵', '🟣', '⚫', '⚪'];

function paramsForWis(myMod, theirMod) {
  // Equal: 30s, no hints
  // +2:    35s, 1 hint at 8s
  // +4:    40s, 2 hints at 8s and 18s
  const diff = myMod - theirMod;
  if (diff >= 4) return { durationMs: 40000, hintAtMs: [8000, 18000] };
  if (diff >= 2) return { durationMs: 35000, hintAtMs: [8000] };
  return            { durationMs: 30000, hintAtMs: [] };
}

export default {
  id: 'spotDifference',
  label: 'Spot the Difference',
  stat: 'wis',
  icon: '👁',
  description: 'Two grids, a few cells differ. Tap differences. Higher WIS gives you more time and hints.',
  paramsFromStats(myScore, theirScore) {
    return paramsForWis(mod(myScore), mod(theirScore));
  },
  Component({ challenge, mySide, params, seed, onScore }) {
    const durationMs = params?.durationMs || 30000;
    const hintAtMs   = params?.hintAtMs   || [];

    // Deterministic generation: same grid + same diff cells for both players.
    const { left, right, diffCells } = useMemo(() => {
      const rng = seededRandom(seed | 0);
      const total = GRID_SIZE * GRID_SIZE;
      const base = [];
      for (let i = 0; i < total; i++) {
        base.push(POOL[Math.floor(rng() * POOL.length)]);
      }
      // Pick DIFF_COUNT distinct cells to mutate on the right.
      const picked = new Set();
      while (picked.size < DIFF_COUNT) {
        picked.add(Math.floor(rng() * total));
      }
      const right = base.slice();
      for (const idx of picked) {
        // Pick a different emoji from the pool.
        let next = base[idx];
        while (next === base[idx]) next = POOL[Math.floor(rng() * POOL.length)];
        right[idx] = next;
      }
      return { left: base, right, diffCells: picked };
    }, [seed]);

    const startedAt = useRef(null);
    const submitted = useRef(false);
    const [now,    setNow]    = useState(0);
    const [found,  setFound]  = useState(new Set());        // cell indices identified
    const [misses, setMisses] = useState(0);
    const [hintCell, setHintCell] = useState(null);
    const [done,   setDone]   = useState(false);

    useEffect(() => {
      startedAt.current = Date.now();
      let raf;
      const tick = () => {
        const elapsed = Date.now() - startedAt.current;
        if (elapsed >= durationMs) {
          setNow(durationMs);
          setDone(true);
          return;
        }
        setNow(elapsed);
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }, [durationMs]);

    // Schedule WIS hints — briefly highlight one undiscovered difference.
    useEffect(() => {
      const timeouts = [];
      hintAtMs.forEach((t) => {
        const id = setTimeout(() => {
          // Pick any undiscovered diff cell.
          const undiscovered = [...diffCells].filter((c) => !found.has(c));
          if (undiscovered.length === 0) return;
          const pick = undiscovered[Math.floor(Math.random() * undiscovered.length)];
          setHintCell(pick);
          const fade = setTimeout(() => setHintCell(null), 1500);
          timeouts.push(fade);
        }, t);
        timeouts.push(id);
      });
      return () => { timeouts.forEach(clearTimeout); };
      // intentionally only once; hintAtMs is fixed for the run
    }, [diffCells, hintAtMs]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (done && !submitted.current) {
        submitted.current = true;
        const score = Math.max(0, found.size - misses);
        onScore(score);
      }
    }, [done, found, misses, onScore]);

    // All found? Auto-submit early.
    useEffect(() => {
      if (found.size >= DIFF_COUNT && !done) {
        setDone(true);
        setNow(durationMs);
      }
    }, [found, done, durationMs]);

    const handleCell = (idx) => {
      if (done) return;
      if (found.has(idx)) return;
      if (diffCells.has(idx)) {
        setFound((prev) => {
          const next = new Set(prev);
          next.add(idx);
          return next;
        });
      } else {
        setMisses((m) => m + 1);
      }
    };

    const remainingS = Math.max(0, (durationMs - now) / 1000);

    const renderGrid = (cells, isRight) => (
      <div style={styles.grid}>
        {cells.map((em, idx) => {
          const isFound  = found.has(idx);
          const isHint   = hintCell === idx;
          const cellStyle = {
            ...styles.cell,
            ...(isFound ? styles.cellFound : {}),
            ...(isHint  ? styles.cellHint  : {}),
          };
          return (
            <button key={idx} style={cellStyle} onClick={() => handleCell(idx)}>
              <span style={styles.cellEmoji}>{em}</span>
            </button>
          );
        })}
      </div>
    );

    return (
      <div style={styles.wrap}>
        <div style={styles.title}>Spot the Difference</div>
        <div style={styles.headerRow}>
          <span style={styles.timer}>⏳ {remainingS.toFixed(1)}s</span>
          <span style={styles.score}>Found: {found.size}/{DIFF_COUNT}</span>
          {misses > 0 && <span style={styles.misses}>Misses: {misses}</span>}
        </div>
        <div style={styles.gridsRow}>
          {renderGrid(left, false)}
          <div style={styles.divider} />
          {renderGrid(right, true)}
        </div>
        <div style={styles.hint}>Tap any cell that differs between the two grids.</div>
      </div>
    );
  },
};

const styles = {
  wrap:      { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: 12 },
  title:     { fontSize: 20, fontWeight: 800, color: '#d4a847', fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.06em' },
  headerRow: { display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, fontWeight: 700, color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace" },
  timer:     { background: '#0e1528', border: '1px solid #4a3d28', padding: '4px 10px', borderRadius: 4, color: '#9a8b6e' },
  score:     { background: 'rgba(212,168,71,0.15)', border: '1px solid #8b7340', padding: '4px 10px', borderRadius: 4, color: '#d4a847' },
  misses:    { background: 'rgba(192,57,43,0.15)', border: '1px solid #c03333', padding: '4px 10px', borderRadius: 4, color: '#f87171' },
  gridsRow:  { display: 'flex', alignItems: 'center', gap: 6 },
  grid:      { display: 'grid', gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`, gap: 2, width: 'min(170px, 38vw)', aspectRatio: '1' },
  cell:      { background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 3, padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cellFound: { background: 'rgba(74,154,90,0.25)', border: '1px solid #5cb870', boxShadow: '0 0 6px rgba(92,184,112,0.5)' },
  cellHint:  { boxShadow: '0 0 14px #facc15', border: '1px solid #facc15', animation: 'pulse 0.6s ease-in-out infinite' },
  cellEmoji: { fontSize: 'clamp(14px, 4vw, 22px)', lineHeight: 1 },
  divider:   { width: 1, alignSelf: 'stretch', background: '#4a3d28' },
  hint:      { fontSize: 11, color: '#9a8b6e', fontStyle: 'italic', textAlign: 'center' },
};
